// A shared SSE connection owns one local revision watcher. It never reads GitHub or a board.
export const LOCAL_REVISION_INTERVAL_MS = 60000;
export const LOCAL_REVISION_TIMEOUT_MS = 10000;

export function createLocalRevisionWatcher({ spaceId, token, initialRevision = null, onChange, onForbidden, currentToken, fetcher = fetch }) {
  let stopped = false, revision = initialRevision, observation = 0, running = false, controller;
  const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";
  const current = () => !stopped && currentToken() === token;
  function observe(value) {
    if (value == null || stopped) return;
    observation++;
    revision = value;
  }
  async function check() {
    if (!current() || !visible() || running) return;
    running = true;
    const before = observation;
    controller = new AbortController();
    const owned = controller;
    let timeout;
    // The physical request owns the slot until headers AND body settle. Abort is
    // cooperative: a timeout must not spawn more reads while an adapter ignores it.
    const physical = (async () => {
      const response = await fetcher(`/api/cicd/events/revision?spaceId=${encodeURIComponent(spaceId)}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, signal: owned.signal, cache: "no-store",
      });
      if ([401, 403].includes(response.status)) return { forbidden: true };
      if (!response.ok) throw new Error("CICD_REVISION_UNAVAILABLE");
      const body = await response.json();
      if (body?.spaceId !== spaceId || typeof body.revision !== "string" || !body.revision) throw new Error("CICD_REVISION_INVALID");
      return body;
    })().finally(() => { running = false; });
    try {
      // Promise.race also handles a late physical rejection after the deadline.
      const result = await Promise.race([
        physical,
        new Promise((_, reject) => { timeout = setTimeout(() => { owned.abort(); reject(new Error("CICD_REVISION_TIMEOUT")); }, LOCAL_REVISION_TIMEOUT_MS); }),
      ]);
      if (!current()) return;
      if (result.forbidden) { stop(); onForbidden(); return; }
      // An SSE observation received during the read is newer than this request's baseline.
      if (before !== observation) return;
      const changed = revision !== null && result.revision !== revision;
      observe(result.revision);
      if (changed) onChange(result.revision);
    } catch { /* Retain the known revision; the next bounded local check retries. */ }
    finally { clearTimeout(timeout); }
  }
  const resume = () => { if (visible()) check(); };
  const timer = setInterval(check, LOCAL_REVISION_INTERVAL_MS);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", resume);
  function stop() {
    stopped = true;
    clearInterval(timer);
    controller?.abort();
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", resume);
  }
  return { observe, check, stop };
}
